import { Flex, Text } from '@backstage/ui';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import { getTypeOrNull } from '@spotify/confidence-graphql';
import { useFlag } from '@spotify/plugin-confidence-flags-react';

import {
  PreviewShell,
  RuleItem,
  StateTag,
  SummarySection,
  VariantItem,
  buildVariantUsage,
} from './components';

interface FlagArtifactPreviewProps {
  resourceName: string;
}

export const FlagArtifactPreview = ({
  resourceName,
}: FlagArtifactPreviewProps) => {
  const { flag, isLoading, error } = useFlag({ name: resourceName });

  const previewError = error ?? (!isLoading && !flag ? 'not-found' : undefined);
  const variantUsage = buildVariantUsage(flag?.rules ?? []);

  return (
    <PreviewShell loading={isLoading} error={previewError}>
      {flag && (
        <>
          <Flex direction="column" gap="4">
            <StateTag state={flag.state} />

            {flag.description && (
              <Text variant="body-small">{flag.description}</Text>
            )}
          </Flex>

          {flag.clients.length > 0 && (
            <SummarySection title="Clients">
              <Text variant="body-small">
                {flag.clients
                  .map(client => getTypeOrNull(client))
                  .filter((c): c is NonNullable<typeof c> => Boolean(c))
                  .map(
                    client =>
                      client.displayName ||
                      extractLastNameComponent(client.name) ||
                      client.name,
                  )
                  .join(', ')}
              </Text>
            </SummarySection>
          )}

          {flag.variants.length > 0 && (
            <SummarySection title="Variants">
              <Flex direction="column" gap="2">
                {flag.variants.map(variant => (
                  <VariantItem
                    key={variant.name}
                    variant={variant}
                    usageCount={variantUsage[variant.name] ?? 0}
                  />
                ))}
              </Flex>
            </SummarySection>
          )}
          <SummarySection title="Rules">
            {flag.rules.length === 0 ? (
              <Text variant="body-small" color="secondary">
                No rules.
              </Text>
            ) : (
              <Flex direction="column" gap="3">
                {flag.rules.map(rule => (
                  <RuleItem key={rule.name} rule={rule} />
                ))}
              </Flex>
            )}
          </SummarySection>
        </>
      )}
    </PreviewShell>
  );
};
